import type { ShopperProducts, ShopperSearch } from '@/scapi';
import type { ProductButtonKind } from '@/features/product-button';

export type ProductCardItem = ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'];

type EnrichedProductSearchHit = ShopperSearch.schemas['ProductSearchHit'] & {
    c_brand?: unknown;
    c_availabilityStatus?: string;
    c_primaryAction?: { type?: string; enabled?: boolean; reason?: string };
    c_custom?: {
        brand?: unknown;
        badges?: unknown;
        quickBuyAttributes?: unknown;
        averageRating?: unknown;
        reviewCount?: unknown;
    };
};

export const isProductSearchHit = (product: ProductCardItem): product is ShopperSearch.schemas['ProductSearchHit'] =>
    'productId' in product;

// Ratings arrive as strings ("3.7235"); 0 keeps the "(0)" rating visible on tiles without reviews.
const toRatingNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getAvailabilityOrderable = ({ c_availabilityStatus: status }: EnrichedProductSearchHit) =>
    status == null ? undefined : status !== 'NOT_AVAILABLE';

type ImageGroup = ShopperProducts.schemas['ImageGroup'];
type Image = ShopperProducts.schemas['Image'];

// Hit images carry `sw=582&sh=582`; DynamicImage only swaps `sw`, so a leftover `sh` crops the tile.
const stripDisSize = (link: string | undefined): string | undefined => {
    if (!link) return link;
    try {
        const url = new URL(link);
        url.searchParams.delete('sw');
        url.searchParams.delete('sh');
        return url.toString();
    } catch {
        return link;
    }
};

const withoutDisSize = (image: Image): Image => ({
    ...image,
    link: stripDisSize(image.link) as string,
    disBaseLink: stripDisSize(image.disBaseLink),
});

// Masters can return swatch-only groups; fall back to the hit's main image rather than a swatch.
const getTileImageGroups = (groups: ImageGroup[] | undefined, image: Image | undefined): ImageGroup[] | undefined => {
    const normalised = groups?.map((group) => ({ ...group, images: group.images?.map(withoutDisSize) }));
    const hasLarge = normalised?.some((group) => group.viewType === 'large');
    if (hasLarge || !image) return normalised;
    return [{ viewType: 'large', images: [withoutDisSize(image)] }, ...(normalised ?? [])];
};

/** Build a card projection from a compact search hit without making an extra request per PLP tile. */
export const getProductFromGridItem = (item: ProductCardItem): ShopperProducts.schemas['Product'] => {
    if (!isProductSearchHit(item)) return item;

    const represented = (item.representedProduct ?? {}) as unknown as ShopperProducts.schemas['Product'];
    const enrichedItem = item as EnrichedProductSearchHit;
    const custom = enrichedItem.c_custom;
    const nestedCustomBrand = custom?.brand;
    const customBrand = enrichedItem.c_brand;
    const hitInventory = item.inventory as { ats?: number } | undefined;
    const hitOrderable = (item as { orderable?: boolean }).orderable;
    const imageGroups = getTileImageGroups(item.imageGroups ?? represented.imageGroups, item.image);

    return {
        ...represented,
        // Search hits can carry fresher purchase metadata than representedProduct. Preserve it so
        // ProductButton can resolve Quick Buy, variation selection and purchase restrictions.
        ...item,
        id: item.productId,
        name: item.productName,
        brand:
            item.brand ??
            (typeof nestedCustomBrand === 'string' ? nestedCustomBrand : undefined) ??
            (typeof customBrand === 'string' ? customBrand : undefined) ??
            represented.brand,
        price: item.price ?? represented.price,
        priceMax: item.priceMax ?? represented.priceMax,
        currency: item.currency ?? represented.currency,
        imageGroups,
        variationAttributes: item.variationAttributes ?? represented.variationAttributes,
        variants: item.variants ?? represented.variants,
        type: item.productType ?? represented.type,
        inventory: {
            ...represented.inventory,
            ...hitInventory,
            orderable:
                hitOrderable ??
                item.inStock ??
                (hitInventory?.ats == null
                    ? (getAvailabilityOrderable(enrichedItem) ?? represented.inventory?.orderable)
                    : hitInventory.ats > 0),
        },
        c_custom: custom,
        c_badges: custom?.badges,
        c_quickBuyAttributes: custom?.quickBuyAttributes,
        c_bvAverageRating: toRatingNumber(custom?.averageRating),
        c_bvReviewCount: toRatingNumber(custom?.reviewCount),
    } as ShopperProducts.schemas['Product'];
};

/**
 * Maps the hit's `c_primaryAction` to a CTA kind where `ProductButton` can't derive it from the hit
 * (search hits carry no notify-me flag); `undefined` lets it apply its own stock / restriction rules.
 */
export const getProductCardKind = (item: ProductCardItem): ProductButtonKind | undefined => {
    const action = (item as EnrichedProductSearchHit).c_primaryAction;
    switch (action?.type) {
        case 'ADD_TO_BAG':
            return action.enabled === false ? 'out-of-stock' : undefined;
        case 'NOTIFY_ME':
            return 'notify-me';
        case 'SELECT_VARIANT':
            return 'quick-buy';
        default:
            return undefined;
    }
};
